'use client'

import { useTranslations } from 'next-intl'
import { Fragment, ReactNode } from 'react'

import ModalFooter from '../../modal/components/footer'
import modalStore from '../../stores/modal-store'
import { Draggable, DraggableProvided } from '@hello-pangea/dnd'
import { ChevronRightIcon, HamburgerMenuIcon } from '@radix-ui/react-icons'
import { Table, Button, ChevronDownIcon, Tooltip, DropdownMenu } from '@radix-ui/themes'

import { useTableContext, useTableStore } from '../context/table-context'
import { TableActions, TableActionsDropdownMenuContent } from '../styled'
import { TableAction } from '../types'
import TableRowSelectBox from './table-row-select'
import { TLeadingCell, useActionErrorHandler, useTableLayout } from './table-utils'
import { GetValueByPath } from '../../utils'

type TProps<T> = {
    row: T
    /** position among the rendered rows (drag & drop index) */
    index: number
    expandedRows: (string | number)[]
    handleRowExpansion: (id: string | number) => void
}

type TActionWithHandler<T> = Extract<TableAction<T>, { action: unknown }>

function TableRow<T>({ row, index, expandedRows, handleRowExpansion }: TProps<T>) {
    const t = useTranslations('table')

    const {
        store,
        serverSide,
        draggable,
        id,
        expandable,
        classNameGenerator,
        customRowRender,
        tableActionsBreakpoint,
        generateRowProps,
        actions,
    } = useTableContext<T>()
    const { columns, leadingCells, hasActionsColumn, leadingCellStyle, columnStyle } = useTableLayout<T>()
    const refreshData = useTableStore<T, 'refreshData'>('refreshData')
    const handleError = useActionErrorHandler()

    const actionHandler = async (action: TActionWithHandler<T>) => {
        try {
            await action.action(row)
        }
        catch (error) {
            handleError(
                error,
                action.name === 'delete'
                    ? t('unexpected-error-on-delete')
                    : action.name === 'refresh'
                        ? t('unexpected-error-on-save')
                        : undefined
            )
            return
        }

        // static data cannot be re-fetched, the deleted row is dropped locally
        if (action.name === 'delete' && !serverSide)
            store.removeRow(row, id)
        else if (action.name === 'delete' || action.name === 'refresh')
            refreshData()
    }

    const confirmDelete = (action: TActionWithHandler<T>) => {
        const { pushNewModal, resetLastModal } = modalStore

        pushNewModal({
            title: t('delete-record'),
            description: t('delete-confirm'),
            content: (
                <ModalFooter
                    onClose={() => resetLastModal()}
                    onConfirm={async () => {
                        await actionHandler(action)
                        resetLastModal()
                    }}
                />
            ),
        })
    }

    const runAction = (action: TActionWithHandler<T>) =>
        action.name == 'delete' ? confirmDelete(action) : actionHandler(action)

    // Plain render functions: a component declared inside TableRow would get a new identity on every
    // render and React would remount all action buttons of every row.
    const renderActionButton = (action: TableAction<T>, key: number) => {
        if ('render' in action)
            return <Fragment key={key}>{action.render(row)}</Fragment>

        const button = (
            <Button
                key={key}
                color={action.name == 'delete' ? 'red' : undefined}
                disabled={!!action.disabled}
                onClick={() => runAction(action)}
            >
                {action.label}
            </Button>
        )

        return typeof action.disabled == 'string'
            ? <Tooltip key={key} content={action.disabled}>{button}</Tooltip>
            : button
    }

    // the item itself is the control, so Enter / Space trigger the action as well
    const renderActionMenuItem = (action: TableAction<T>, key: number) => {
        if ('render' in action)
            return <DropdownMenu.Item key={key}>{action.render(row)}</DropdownMenu.Item>

        const item = (
            <DropdownMenu.Item
                key={key}
                color={action.name == 'delete' ? 'red' : undefined}
                disabled={!!action.disabled}
                onSelect={() => runAction(action)}
            >
                {action.label}
            </DropdownMenu.Item>
        )

        return typeof action.disabled == 'string'
            ? <Tooltip key={key} content={action.disabled}>{item}</Tooltip>
            : item
    }

    const ExpandedRowRendered = expandable?.expandedRowRender
        ? expandable.expandedRowRender(row)
        : undefined

    const rowActions = actions.filter(action =>
        'hidden' in action ? !(action.hidden?.(row) ?? false) : true
    )

    const renderLeadingCell = (cell: TLeadingCell, cellIndex: number, drag?: DraggableProvided) => {
        const style = leadingCellStyle(cellIndex)

        switch (cell) {
            case 'drag':
                return (
                    <Table.Cell key={cell} style={style}>
                        <div {...drag?.dragHandleProps}>
                            <HamburgerMenuIcon />
                        </div>
                    </Table.Cell>
                )
            case 'select':
                return <TableRowSelectBox key={cell} row={row} style={style} />
            case 'expand':
                return (
                    <Table.Cell key={cell} className="table__expand-button" style={style}>
                        {typeof ExpandedRowRendered !== 'undefined' && (
                            <Button
                                variant="soft"
                                onClick={() => handleRowExpansion(row[id] as string | number)}
                            >
                                {expandedRows.includes(row[id] as string | number) ? (
                                    <ChevronDownIcon />
                                ) : (
                                    <ChevronRightIcon />
                                )}
                            </Button>
                        )}
                    </Table.Cell>
                )
        }
    }

    const renderRow = (drag?: DraggableProvided) => {
        const rowProps = generateRowProps?.(row)

        return (
            <Table.Row
                className={classNameGenerator ? classNameGenerator(row) : ''}
                {...rowProps}
                {...drag?.draggableProps}
                ref={drag?.innerRef}
                style={drag ? { ...rowProps?.style, ...drag.draggableProps.style } : rowProps?.style}
            >
                {leadingCells.map((cell, cellIndex) => renderLeadingCell(cell, cellIndex, drag))}
                {!!customRowRender
                    ? customRowRender(row)
                    : columns.map(({ accessor, accessor2, formatter, className }, columnIndex) => {
                        const cellValue = (GetValueByPath(row, accessor) ??
                            (accessor2 ? GetValueByPath(row, accessor2) : '')) as ReactNode
                        return (
                            <Table.Cell
                                className={`${className ?? ''} fixed`}
                                key={columnIndex}
                                style={columnStyle(columnIndex)}
                            >
                                {formatter ? formatter(cellValue, row) : cellValue}
                            </Table.Cell>
                        )
                    })}
                {/* always rendered (empty when every action is hidden), the header has the column */}
                {hasActionsColumn && (
                    <Table.Cell className="fixed">
                        <TableActions>
                            {rowActions.length > tableActionsBreakpoint ? (
                                <DropdownMenu.Root>
                                    <DropdownMenu.Trigger>
                                        <Button>
                                            <HamburgerMenuIcon />
                                        </Button>
                                    </DropdownMenu.Trigger>
                                    <TableActionsDropdownMenuContent>
                                        {rowActions.map(renderActionMenuItem)}
                                    </TableActionsDropdownMenuContent>
                                </DropdownMenu.Root>
                            ) : (
                                rowActions.map(renderActionButton)
                            )}
                        </TableActions>
                    </Table.Cell>
                )}
            </Table.Row>
        )
    }

    return (
        <>
            {draggable ? (
                <Draggable draggableId={String(row[id])} index={index}>
                    {provided => renderRow(provided)}
                </Draggable>
            ) : (
                renderRow()
            )}

            {typeof ExpandedRowRendered !== 'undefined' &&
                expandedRows.includes(row[id] as string | number) && (
                <Table.Row>
                    <Table.Cell colSpan={leadingCells.length} />
                    <Table.Cell colSpan={columns.length + (hasActionsColumn ? 1 : 0)}>
                        {ExpandedRowRendered}
                    </Table.Cell>
                </Table.Row>
            )}
        </>
    )
}

export default TableRow
